import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Element, animateScroll } from 'react-scroll';
import {
  AutoSizer,
  InfiniteLoader,
  List,
  WindowScroller,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import { useMediaQuery } from 'react-responsive';
import { ReviewItem } from './ReviewItem';
import { Modal } from '@/component/Modal';
import { useParams } from 'next/navigation';
import { useReadQuery } from '@apollo/client';

const HEIGHT = 200;
const TWO_COLS_WIDTH = 628;

export function ReviewsModal({
  reviews,
  rating,
  count,
  queryRef,
  fetchMore,
  loadedPages,
  LIMIT,
  ...props
}) {
  const scrollElement = useRef();
  const params = useParams();
  const t = useTranslations();
  const isMobile = useMediaQuery({ query: '(max-width: 700px)' });

  const { data } = useReadQuery(queryRef);

  const cache = new CellMeasurerCache({
    defaultHeight: HEIGHT,
    fixedWidth: true,
    fixedHeight: false,
  });
  const bodyId = 'reviews-container';
  const header = (
    <div
      className="text-base md:text-2xl font-semibold flex gap-1 items-center max-md:order-2"
      data-testid="room-reviews-header"
    >
      <i className="icon-star_line_32 text-primary-700 text-[32px] max-md:hidden" />
      <i className="icon-star_fill text-primary-700 text-[24px] md:hidden" />
      <span className="text-primary-700">{rating}</span>
      <span className="text-slate-700 ml-2 md:ml-[14px]">
        {count}&nbsp;<span>{t('apartment.rating.sub-title')}</span>
      </span>
    </div>
  );

  const items =
    data?.showListing?.reviews.collection || reviews.collection || [];

  useEffect(() => {
    if (props.show && props.show !== true) {
      setTimeout(() => {
        let scrollTop = 0;
        switch(props.show) {
          case 6:
          case 5:
            scrollTop += isMobile
              ? cache.getHeight(0) + cache.getHeight(1) + cache.getHeight(2) + cache.getHeight(3)
              : Math.max(cache.getHeight(0), cache.getHeight(1)) + Math.max(cache.getHeight(2), cache.getHeight(3));
            break;
          case 4:
          case 3:
            scrollTop += isMobile
              ? cache.getHeight(0) + cache.getHeight(1)
              : Math.max(cache.getHeight(0), cache.getHeight(1));
            break;
          default:
            scrollTop = 0;
        }
        animateScroll.scrollTo(scrollTop, {
          duration: 0,
          delay: 100,
          smooth: false,
          containerId: bodyId,
        });
      }, 100);
    }
  }, []);

  const loadMoreRows = ({ stopIndex }) => {
    const page = parseInt(stopIndex / LIMIT) + 1;

    if (!loadedPages.includes(page)) {
      loadedPages.push(page);

      return fetchMore({
        variables: {
          slug: params.slug,
          page,
          limit: LIMIT,
        },
        updateQuery(previousData, { fetchMoreResult }) {
          const { reviews, ...rest } = previousData.showListing;
          const { collection, ...reviewsRest } = reviews;

          return {
            showListing: {
              ...rest,
              reviews: {
                ...reviewsRest,
                collection: [
                  ...previousData.showListing.reviews.collection,
                  ...fetchMoreResult.showListing.reviews.collection,
                ],
              },
            },
          };
        },
      });
    }
  };

  return (
    <Modal
      {...props}
      scrollElementRef={scrollElement}
      bodyId={bodyId}
      header={header}
    >
      <AutoSizer className="!w-full [&>div]:!w-full" disableHeight>
        {({ width }) => {
          const oneColumn = width < TWO_COLS_WIDTH ? true : false;
          const rowCount = oneColumn ? count : Math.ceil(count / 2);

          return (
            <WindowScroller scrollElement={scrollElement.current}>
              {({
                height,
                isScrolling,
                onChildScroll,
                scrollTop,
                registerChild,
              }) => (
                <div className="mb-[-46px]" ref={(el) => registerChild(el)}>
                  <InfiniteLoader
                    rowCount={count || HEIGHT}
                    loadMoreRows={loadMoreRows}
                    isRowLoaded={({ index }) =>
                      oneColumn ? items[index] : items[index * 2]
                    }
                    minimumBatchSize={LIMIT / 2}
                  >
                    {({ onRowsRendered, registerChild }) => (
                      <List
                        ref={registerChild}
                        width={width}
                        autoHeight
                        height={height}
                        rowCount={rowCount}
                        rowHeight={cache.rowHeight}
                        deferredMeasurementCache={cache}
                        rowRenderer={({ key, parent, index, columnIndex, style }) => {
                          const item = items[index];
                          return item ? (
                            <CellMeasurer
                              cache={cache}
                              columnIndex={columnIndex}
                              key={key}
                              parent={parent}
                              rowIndex={index}
                            >
                              {({ registerChild }) =>
                                oneColumn ? (
                                  <Element
                                    name={`review-${index + 1}`}
                                    style={style}
                                  >
                                    <ReviewItem
                                      classImport="pb-[46px]"
                                      registerChild={registerChild}
                                      id={index + 1}
                                      {...item}
                                      showMore
                                    />
                                  </Element>
                                ) : (
                                  <div
                                    className="grid grid-cols-2 gap-x-4"
                                    ref={registerChild}
                                    style={style}
                                  >
                                    {items[index * 2] ? (
                                      <Element
                                        id={`review-${index * 2 + 1}`}
                                        name={`review-${index * 2 + 1}`}
                                      >
                                        <ReviewItem
                                          classImport="pb-[46px]"
                                          id={index * 2 + 1}
                                          {...items[index * 2]}
                                          showMore
                                        />
                                      </Element>
                                    ) : null}
                                    {items[index * 2 + 1] ? (
                                      <Element
                                        id={`review-${index * 2 + 1 + 1}`}
                                        name={`review-${index * 2 + 1 + 1}`}
                                      >
                                        <ReviewItem
                                          classImport="pb-[46px]"
                                          id={index * 2 + 1 + 1}
                                          {...items[index * 2 + 1]}
                                          showMore
                                        />
                                      </Element>
                                    ) : null}
                                  </div>
                                )
                              }
                            </CellMeasurer>
                          ) : null;
                        }}
                        isScrolling={isScrolling}
                        scrollTop={scrollTop}
                        onScroll={onChildScroll}
                        onRowsRendered={onRowsRendered}
                      />
                    )}
                  </InfiniteLoader>
                </div>
              )}
            </WindowScroller>
          );
        }}
      </AutoSizer>
    </Modal>
  );
}
